import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, SlidersHorizontal, MessageCircle, Eye, Car, Calendar, Gauge, Zap, Mail, Share2 } from 'lucide-react';
import { carPath } from '../lib/carRoutes';

const API = process.env.REACT_APP_BACKEND_URL;
const COMPANY_EMAIL = "abbewray@runtheraysintl.com";
const WHATSAPP = "233544205844";

const CarShowroom = () => {
  const [cars, setCars] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [makeFilter, setMakeFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCars = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/cars?status=available`);
      setCars(data.data);
      setFiltered(data.data);
    } catch (err) {
      console.error('Failed to fetch cars:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCars();
  }, [fetchCars]);

  useEffect(() => {
    let result = cars;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(c => c.title.toLowerCase().includes(q) || c.make.toLowerCase().includes(q) || c.model.toLowerCase().includes(q));
    }
    if (makeFilter) {
      result = result.filter(c => c.make === makeFilter);
    }
    setFiltered(result);
  }, [cars, search, makeFilter]);

  const makes = [...new Set(cars.map(c => c.make))].sort();

  const getImageUrl = (car) => {
    if (car.thumbnail) {
      const path = car.thumbnail.replace('rtr-cars/cars/', '');
      return `${API}/api/cars/images/${path}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/cars/images/eb666297-f67d-4758-995c-0702d5dcbde1.png`} alt="RunTheRays" className="h-10 md:h-12 w-auto" />
            <div>
              <h1 className="text-lg md:text-xl font-bold text-white tracking-tight">RunTheRays <span className="text-orange-500">Automobile</span></h1>
              <p className="text-xs text-gray-400 hidden sm:block">Premium Car Showroom</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`mailto:${COMPANY_EMAIL}?subject=Car Inquiry`}
              className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              data-testid="header-email-btn"
            >
              <Mail size={16} />
              <span className="hidden sm:inline">Email Us</span>
            </a>
            <a
              href={`https://wa.me/${WHATSAPP}?text=Hi! I'd like to know more about your cars.`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              data-testid="header-whatsapp-btn"
            >
              <MessageCircle size={16} />
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-900 to-gray-950 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-3">Our <span className="text-orange-500">Collection</span></h2>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto mb-8">
            Handpicked premium vehicles at RunTheRays Automobile. Browse our inventory and inquire directly via WhatsApp.
          </p>
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span>24/7 Service — We're always available</span>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="text"
              placeholder="Search by name, make or model..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
              data-testid="car-search-input"
            />
          </div>
          <div className="relative">
            <SlidersHorizontal className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <select
              value={makeFilter}
              onChange={(e) => setMakeFilter(e.target.value)}
              className="bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-8 py-2.5 text-sm text-white appearance-none focus:outline-none focus:border-orange-500 transition-colors min-w-[160px]"
              data-testid="car-make-filter"
            >
              <option value="">All Makes</option>
              {makes.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <p className="text-gray-500 text-sm mt-3">{filtered.length} vehicle{filtered.length !== 1 ? 's' : ''} found</p>
      </section>

      {/* Car Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="bg-gray-900 rounded-xl animate-pulse h-80"></div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Car size={48} className="mx-auto text-gray-700 mb-4" />
            <p className="text-gray-500 text-lg">No vehicles found</p>
            <p className="text-gray-600 text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(car => (
              <CarCard key={car.id} car={car} imageUrl={getImageUrl(car)} />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-gray-500 text-sm">
          <div className="flex items-center justify-center gap-4 mb-3">
            <a href={`mailto:${COMPANY_EMAIL}`} className="flex items-center gap-1.5 text-gray-400 hover:text-orange-500 transition-colors">
              <Mail size={14} /> {COMPANY_EMAIL}
            </a>
          </div>
          <p>&copy; {new Date().getFullYear()} RunTheRays Automobile. All rights reserved.</p>
          <p className="mt-1">Shanghai RunTheRays International Trade Co., Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

const CarCard = ({ car, imageUrl }) => {
  const whatsappMsg = encodeURIComponent(`Hi! I am interested in the ${car.title}.`);
  const shareListing = async () => {
    const url = `${window.location.origin}${carPath(`/cars/${car.slug}`)}`;
    if (navigator.share) {
      try { await navigator.share({ title: car.title, text: `Check out this ${car.title}`, url }); } catch (err) { console.error('Share failed:', err); }
    } else {
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };
  return (
    <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-gray-700 transition-all group" data-testid={`car-card-${car.id}`}>
      <Link to={carPath(`/cars/${car.slug}`)} className="block">
        <div className="relative aspect-[16/10] bg-gray-800 overflow-hidden">
          {imageUrl ? (
            <img src={imageUrl} alt={car.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Car size={48} className="text-gray-700" />
            </div>
          )}
          <div className="absolute top-3 left-3">
            {car.images && <span className="bg-black/70 text-white text-xs px-2 py-1 rounded-md">{car.images.length} photos</span>}
          </div>
          {car.status === 'sold' && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="bg-red-600 text-white text-sm font-bold px-4 py-2 rounded-lg">SOLD</span>
            </div>
          )}
        </div>
      </Link>
      <div className="p-4">
        <Link to={carPath(`/cars/${car.slug}`)}>
          <h3 className="font-semibold text-white text-base mb-2 group-hover:text-orange-500 transition-colors">{car.title}</h3>
        </Link>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-400 mb-3">
          <span className="flex items-center gap-1.5"><Calendar size={13} />{car.year}</span>
          <span className="flex items-center gap-1.5"><Gauge size={13} />{car.mileage}</span>
          <span className="flex items-center gap-1.5"><Zap size={13} />{car.engine}</span>
          <span className="flex items-center gap-1.5"><Car size={13} />{car.power}</span>
        </div>
        <div className="mb-3">
          {car.show_price && car.price ? (
            <span className="text-orange-500 font-bold text-lg">${car.price.toLocaleString()}</span>
          ) : (
            <span className="text-gray-400 text-sm italic">Inquire for Price</span>
          )}
        </div>
        <div className="flex gap-2">
          <a
            href={`https://wa.me/${WHATSAPP}?text=${whatsappMsg}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm py-2 rounded-lg font-medium transition-colors"
            data-testid={`whatsapp-btn-${car.id}`}
          >
            <MessageCircle size={14} /> WhatsApp
          </a>
          <a
            href={`mailto:${COMPANY_EMAIL}?subject=Inquiry: ${encodeURIComponent(car.title)}`}
            className="flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-3 rounded-lg font-medium transition-colors"
            data-testid={`email-btn-${car.id}`}
          >
            <Mail size={14} />
          </a>
          <button
            onClick={shareListing}
            className="flex items-center justify-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-white text-sm py-2 px-3 rounded-lg font-medium transition-colors"
            data-testid={`share-btn-${car.id}`}
          >
            <Share2 size={14} />
          </button>
          <Link
            to={carPath(`/cars/${car.slug}`)}
            className="flex items-center justify-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-white text-sm py-2 px-4 rounded-lg font-medium transition-colors"
            data-testid={`details-btn-${car.id}`}
          >
            <Eye size={14} /> Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CarShowroom;
